/**
 * The placement audit trail for one notice.
 *
 * This is the only record of *who demoted the alert* — placement is the one
 * lever an admin has over an Ivanti-sourced notice, and "it stopped showing on
 * the home page and nobody knows why" is the failure this exists to answer.
 * Every placement change appends an event server-side; this dialog just reads
 * them back, newest first, exactly as the API ordered them.
 */

import { Dialog, Timeline, type TimelineItem, type TimelineTone } from "@norican/react";

import { EmptyStateBlock, LoadingState, RelativeTime } from "@/components/common";
import { useT } from "@/i18n";
import type { MessagePlacement } from "@/lib/api";

import { useMessageHistory } from "./api";
import { useMessagePlacementLabel } from "./message-kinds";

/** Where the notice ended up decides the marker colour, not where it came from. */
const PLACEMENT_TONE: Record<MessagePlacement, TimelineTone> = {
  HOME: "accent",
  CENTER: "default",
  HIDDEN: "warning",
};

export interface MessageHistoryDialogProps {
  /** The notice to trace, or `null` when the dialog is closed. */
  messageId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MessageHistoryDialog({ messageId, open, onOpenChange }: MessageHistoryDialogProps) {
  const { t } = useT("messages");
  const placementLabel = useMessagePlacementLabel();
  const query = useMessageHistory(open ? messageId : null);

  const items: TimelineItem[] = (query.data ?? []).map((event, index) => ({
    id: `${event.at}-${index}`,
    // `from` equal to `to` is how the backend records the opening placement —
    // there was nowhere to move it from.
    title:
      event.from_placement === event.to_placement
        ? t("history.created", { to: placementLabel(event.to_placement) })
        : t("history.moved", {
            from: placementLabel(event.from_placement),
            to: placementLabel(event.to_placement),
          }),
    timestamp: <RelativeTime value={event.at} mode="absolute" />,
    description: event.note == null ? event.actor : `${event.actor} — ${event.note}`,
    tone: PLACEMENT_TONE[event.to_placement],
  }));

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup size="md" className="flex max-h-[85vh] w-full flex-col">
          <Dialog.Header>
            <Dialog.Title>{t("history.title")}</Dialog.Title>
            <Dialog.CloseButton label={t("form.cancel")} />
          </Dialog.Header>
          <Dialog.Body className="overflow-y-auto">
            {query.isLoading ? (
              <LoadingState />
            ) : query.error != null ? (
              <EmptyStateBlock
                title={t("history.loadFailed")}
                description={t("history.loadFailedHint")}
                size="sm"
              />
            ) : items.length === 0 ? (
              <EmptyStateBlock title={t("history.empty")} description={null} size="sm" />
            ) : (
              <Timeline items={items} />
            )}
          </Dialog.Body>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
